'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import RisingParticlesScene from './RisingParticlesScene';

// --- Types ---
interface PainPoint {
    id: string;
    label: string;
    icon: string;
    color: string;
    desc: string;
    scanSteps: string[];
    prescription: {
        diagnosis: string;
        fix: string;
        impact: string;
    };
}

// --- Data ---
const painPoints: PainPoint[] = [
    {
        id: 'traffic',
        label: 'Low Website Traffic',
        icon: 'ChartBarIcon',
        color: 'blue',
        desc: "My site exists, but nobody is visiting.",
        scanSteps: ['Analyzing SEO Meta Tags...', 'Checking Keyword density...', 'Verifying Backlink profile...'],
        prescription: {
            diagnosis: "Low Organic Authority",
            fix: "Implement 'Strategic SEO' to capture high-intent keywords.",
            impact: "Potential 300% traffic increase."
        }
    },
    {
        id: 'local',
        label: 'Invisible Locally',
        icon: 'MapPinIcon',
        color: 'emerald',
        desc: "I'm not showing up on Google Maps.",
        scanSteps: ['Scanning Google Business Profile...', 'Checking NAP Consistency...', 'Analyzing Competing Locations...'],
        prescription: {
            diagnosis: "Weak Local Signals",
            fix: "Optimize GBP & Build Local Citations.",
            impact: "Dominate the 'Near Me' search results."
        }
    },
    {
        id: 'reviews',
        label: 'Reputation Issues',
        icon: 'StarIcon',
        color: 'amber',
        desc: "Bad reviews or no reviews are hurting trust.",
        scanSteps: ['Aggregating Review Sentiment...', 'Checking Response Rates...', 'Scanning Social Mentions...'],
        prescription: {
            diagnosis: "Trust Deficit",
            fix: "Deploy Automated Reputation Management.",
            impact: "Convert 40% more visitors with social proof."
        }
    },
    {
        id: 'conversion',
        label: 'Low Sales/Leads',
        icon: 'CurrencyDollarIcon',
        color: 'rose',
        desc: "People visit, but they don't buy or call.",
        scanSteps: ['Analyzing UX Friction...', 'Checking Call-to-Actions...', 'Testing Page Speed...'],
        prescription: {
            diagnosis: "Conversion Bottlenecks",
            fix: "UX/UI Revamp & CRO Strategy.",
            impact: "Turn existing traffic into paying customers."
        }
    }
];

// --- Components ---

const StepWizard = ({ children, step }: { children: React.ReactNode, step: number }) => {
    return (
        <div className="relative overflow-hidden min-h-[400px]">
            <AnimatePresence mode="wait">
                <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-full flex flex-col items-center justify-center p-2"
                >
                    {children}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

const InteractiveTools = () => {
    const [step, setStep] = useState<number>(1);
    const [selectedPainPoint, setSelectedPainPoint] = useState<PainPoint | null>(null);
    const [url, setUrl] = useState('');
    const [scanningText, setScanningText] = useState('Initializing...');

    // Mock Scanning Effect
    useEffect(() => {
        if (step === 3 && selectedPainPoint) {
            let i = 0;
            setScanningText(selectedPainPoint.scanSteps[0]);

            const interval = setInterval(() => {
                i++;
                if (i < selectedPainPoint.scanSteps.length) {
                    setScanningText(selectedPainPoint.scanSteps[i]);
                } else {
                    clearInterval(interval);
                    setTimeout(() => setStep(4), 800);
                }
            }, 1500);

            return () => clearInterval(interval);
        }
    }, [step, selectedPainPoint]);

    const handleReset = () => {
        setStep(1);
        setSelectedPainPoint(null);
        setUrl('');
    };

    return (
        <section className="py-24 bg-slate-50 relative overflow-hidden">
            {/* Background Particles */}
            <RisingParticlesScene size={0.14} />

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center mb-12">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-6">
                        AI Diagnostic Intelligence
                    </span>
                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
                        What's holding your brand back?
                    </h2>
                    <p className="text-lg text-slate-600">
                        Select a challenge below to generate a personalized growth prescription.
                    </p>
                </div>

                <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden relative min-h-[500px] flex flex-col">
                    {/* Progress Bar */}
                    <div className="h-1 bg-slate-100 w-full flex">
                        <div
                            className="bg-brand-primary transition-all duration-500 ease-in-out"
                            style={{ width: `${(step / 4) * 100}%` }}
                        />
                    </div>

                    <div className="flex-grow flex items-center justify-center p-6 md:p-10">
                        <StepWizard step={step}>
                            {/* STEP 1: SELECT PAIN POINT */}
                            {step === 1 && (
                                <div className="w-full max-w-4xl">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {painPoints.map((point) => (
                                            <button
                                                key={point.id}
                                                onClick={() => {
                                                    setSelectedPainPoint(point);
                                                    setStep(2);
                                                }}
                                                className="group relative flex items-start p-6 rounded-2xl border-2 border-slate-100 hover:border-brand-primary/30 hover:bg-slate-50 transition-all text-left"
                                            >
                                                <div className={`w-12 h-12 rounded-xl bg-${point.color}-100 flex items-center justify-center shrink-0 mr-4 group-hover:scale-110 transition-transform`}>
                                                    <Icon name={point.icon as any} size={24} className={`text-${point.color}-600`} variant="solid" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-900 text-lg mb-1">{point.label}</h3>
                                                    <p className="text-sm text-slate-500 leading-relaxed">{point.desc}</p>
                                                </div>
                                                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400">
                                                    <Icon name="ArrowRightIcon" size={20} />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* STEP 2: ENTER URL */}
                            {step === 2 && selectedPainPoint && (
                                <div className="w-full max-w-lg text-center">
                                    <div className={`w-16 h-16 mx-auto rounded-2xl bg-${selectedPainPoint.color}-100 flex items-center justify-center mb-6`}>
                                        <Icon name={selectedPainPoint.icon as any} size={32} className={`text-${selectedPainPoint.color}-600`} variant="solid" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                                        Let's diagnose your {selectedPainPoint.label}
                                    </h3>
                                    <p className="text-slate-500 mb-8">
                                        Enter your website URL so our diverse intelligence engine can analyze the specific data points.
                                    </p>

                                    <div className="relative mb-6">
                                        <input
                                            type="url"
                                            placeholder="www.yourcompany.com"
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && url && setStep(3)}
                                            autoFocus
                                            className="w-full py-4 pl-6 pr-12 bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-brand-primary focus:ring-0 outline-none text-lg font-medium transition-colors"
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                                            <Icon name="GlobeAltIcon" size={24} />
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <button
                                            onClick={() => setStep(1)}
                                            className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={() => setStep(3)}
                                            disabled={!url}
                                            className="flex-grow py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-slate-900/20"
                                        >
                                            Start Diagnosis
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: SCANNING */}
                            {step === 3 && (
                                <div className="text-center">
                                    <div className="relative w-32 h-32 mx-auto mb-8">
                                        {/* Spinning Rings */}
                                        <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
                                        <div className="absolute inset-0 border-4 border-brand-primary rounded-full border-t-transparent animate-spin" />
                                        <div className="absolute inset-4 border-4 border-brand-secondary rounded-full border-b-transparent animate-spin-slow" />

                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Icon name="CpuChipIcon" size={40} className="text-slate-300 animate-pulse" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2 animate-pulse">
                                        {scanningText}
                                    </h3>
                                    <p className="text-slate-400 text-sm font-mono uppercase tracking-widest">
                                        Processing Logic Core...
                                    </p>
                                </div>
                            )}

                            {/* STEP 4: RESULTS (Prescription) */}
                            {step === 4 && selectedPainPoint && (
                                <div className="w-full max-w-2xl bg-slate-50 rounded-2xl border border-slate-200 p-8 relative overflow-hidden">
                                    {/* Medical/Tech Cross Pattern */}
                                    <div className="absolute top-0 right-0 p-4 opacity-10">
                                        <Icon name="PlusCircleIcon" size={100} className="text-brand-primary" />
                                    </div>

                                    <div className="relative z-10">
                                        <div className="flex items-center gap-3 mb-6">
                                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider rounded-full">
                                                Diagnosis Complete
                                            </span>
                                            <span className="text-slate-400 text-sm">
                                                ID: #8X29-A
                                            </span>
                                        </div>

                                        <h3 className="text-3xl font-black text-slate-900 mb-2">
                                            {selectedPainPoint.prescription.diagnosis}
                                        </h3>
                                        <p className="text-lg text-slate-600 mb-8 border-l-4 border-brand-primary pl-4">
                                            We analyzed <span className="font-semibold text-slate-900">{url}</span> and detected specific patterns limiting your {selectedPainPoint.label.toLowerCase()}.
                                        </p>

                                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                                            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                                                <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Prescribed Rx</h4>
                                                <p className="font-bold text-brand-primary text-lg">
                                                    {selectedPainPoint.prescription.fix}
                                                </p>
                                            </div>
                                            <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
                                                <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">Projected Impact</h4>
                                                <p className="font-bold text-green-600 text-lg">
                                                    {selectedPainPoint.prescription.impact}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex gap-4">
                                            <button
                                                onClick={handleReset}
                                                className="px-6 py-3 rounded-xl text-slate-500 font-bold hover:bg-slate-100 transition-colors"
                                            >
                                                New Scan
                                            </button>
                                            <button className="flex-grow py-3 rounded-xl bg-brand-primary text-white font-bold hover:bg-brand-secondary transition-colors shadow-lg shadow-brand-primary/25 flex items-center justify-center gap-2">
                                                <Icon name="LockClosedIcon" size={20} />
                                                Unlock Full Implementation Plan
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </StepWizard>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InteractiveTools;